import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import SEO, { breadcrumbSchema } from '../components/SEO';
import { contactInfo } from '../mock';

const contactPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Varni Visa',
  url: 'https://www.varnivisa.com/contact',
  mainEntity: {
    '@type': 'Organization',
    name: 'Varni Visa',
    telephone: ['+91-9016930091', '+91-7388813555'],
    email: ['info@varnivisa.com', 'varnivisa@gmail.com'],
    address: {
      '@type': 'PostalAddress',
      streetAddress: '105 Anupam Business Hub, Yogi Chowk',
      addressLocality: 'Surat',
      addressRegion: 'Gujarat',
      addressCountry: 'IN'
    }
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    inquiry_type: 'general',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success('Thank you! Our team will get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', subject: '', inquiry_type: 'general', message: '' });
    } catch (err) {
      toast.error('Failed to submit. Please call us directly at +91 90169 30091');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Varni Visa - Visa Consultant in Surat | +91 90169 30091"
        description="Contact Varni Visa at 105 Anupam Business Hub, Yogi Chowk, Surat. Phone +91 90169 30091, +91 73888 13555. Email info@varnivisa.com. Free consultation."
        keywords="Contact Varni Visa, Visa Consultant Surat Contact, Travel Agency Surat Phone"
        path="/contact"
        schema={[breadcrumbSchema([{name: 'Home', path: '/'}, {name: 'Contact', path: '/contact'}]), contactPageSchema]}
      />
      <section 
        className="relative h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.6), rgba(15, 23, 42, 0.75)), url('https://images.pexels.com/photos/4922085/pexels-photo-4922085.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <img src="/logo.png" alt="Varni Visa" className="h-20 md:h-24 w-auto mx-auto mb-4 object-contain drop-shadow-2xl" />
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Contact Varni Visa</h1>
          <p className="text-lg text-white/90">Surat&apos;s trusted visa & travel consultant — here to help you 7 days a week</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-lg flex items-center justify-center mb-4">
                    <Phone className="text-white" size={20} />
                  </div>
                  <h3 className="font-bold mb-2">Call Us</h3>
                  {contactInfo.phones.map((phone, idx) => (
                    <a key={idx} href={`tel:${phone}`} className="block text-gray-600 hover:text-[#2D8DB4] transition-colors">
                      {phone}
                    </a>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-lg flex items-center justify-center mb-4">
                    <Mail className="text-white" size={20} />
                  </div>
                  <h3 className="font-bold mb-2">Email Us</h3>
                  {contactInfo.emails.map((email, idx) => (
                    <a key={idx} href={`mailto:${email}`} className="block text-gray-600 hover:text-[#2D8DB4] transition-colors">
                      {email}
                    </a>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <h3 className="font-bold mb-2">Visit Us</h3>
                  <p className="text-gray-600 text-sm">{contactInfo.address}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-lg flex items-center justify-center mb-4">
                    <Clock className="text-white" size={20} />
                  </div>
                  <h3 className="font-bold mb-2">Working Hours</h3>
                  <p className="text-gray-600 text-sm">Mon - Sat: 9:30 AM - 7:30 PM</p>
                  <p className="text-gray-600 text-sm">Sunday: Closed</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="type">Inquiry Type</Label>
                        <Select value={formData.inquiry_type} onValueChange={(v) => setFormData({ ...formData, inquiry_type: v })}>
                          <SelectTrigger id="type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Inquiry</SelectItem>
                            <SelectItem value="visa">Visa Services</SelectItem>
                            <SelectItem value="tour">Tour Packages</SelectItem>
                            <SelectItem value="flight">Flight Booking</SelectItem>
                            <SelectItem value="hotel">Hotel Booking</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#2D8DB4] hover:bg-[#247a9a]" size="lg" disabled={submitting}>
                      {submitting ? 'Sending...' : (
                        <>Send Message <Send className="ml-2" size={18} /></>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Embed */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <Card className="overflow-hidden">
            <iframe
              title="Varni Visa Location"
              src="https://www.google.com/maps?q=Yogi+Chowk+Surat+Gujarat&output=embed"
              width="100%"
              height="400"
              style={{ border: 0 }}
              loading="lazy"
            />
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
